import { useEffect, useState } from "react";
import TotalInviteSent from "./applicant-invited/TotalInviteSent";
import PendingLogin from "./applicant-invited/PendingLogin";
import ApplicantLoggedIn from "./applicant-invited/ApplicantLoggedIn";
import ModifyInviteModal from "../../../core/common/ModifyInviteModal";
import TimelineModal from "../../../core/common/TimelineModal";
import { Button } from "antd";
import GuideContentArea from "../../../core/common/GuideContentArea";
import { timelineData_invite } from "../../../core/data/js/dummy-data";
import TabItems from "../../../core/common/TabItems";
import Footer from "../../../core/common/Footer";
import useApi from "../../../core/customhook/useApi";
import ApplicantInProgress from "./applicant-invited/ApplicantInProgress";
import WorkInProgress from "./WorkInProgress";
import { PageLoader } from "../../../core/common/loader/PageLoader";
import webservice_url from "../../../core/webservice/Webservice_url";
import {
  FileTextOutlined,
  ClockCircleOutlined,
  LoginOutlined,
  SyncOutlined,
  FundProjectionScreenOutlined,
} from "@ant-design/icons";

interface CountData {
  status_id: string;
  label: string;
  count: number;
}

const guideData = [
  {
    title: "Stage 1 - Total Invites Sent",
    steps: [
      "All invites sent will by default show a pending login status. The invite will be sent to the candidate, and they have not yet opened the link and logged in to the account.",
    ],
  },
  {
    title: "Stage 2 - Pending LogIn",
    steps: [
      "The invite will be sent to the candidate, and they have not yet opened the link and logged in to the account.",
    ],
  },
  {
    title: "Stage 3 - Logged In",
    steps: [
      "The invite has been opened by the candidate, and they have proceeded to fill the form. Form filling is in progress.",
    ],
  },
  {
    title: "Stage 4 - In Progress",
    steps: [
      "The case has been received by the agency and the background verification process has started.",
      "The agency/verifier is actively working on the case and completing the required verification checks.",
      "The progress of individual checks can be tracked from the case details.",
      "Once all required checks are completed, the case will move to the next applicable stage based on the verification outcome.",
    ],
  },
  {
    title: "Stage 5 - Work In Progress",
    steps: [
      "These are live cases on which the background verification work is currently ongoing.",
      "The count and list for this stage are fetched separately from the live case progress data.",
    ],
  },
];

const ACTIVE_TAB_KEY = "adminCaseTrackActiveTab";

const ApplicantCaseTracking = () => {
  // Data for the 4 invite tabs (existing API)
  const [data, setData] = useState<CountData[]>([]);
  // Data for the Work In Progress tab (separate API)
  const [wipData, setWipData] = useState<CountData[]>([]);

  const getTotalCounts = useApi<any>(webservice_url.admin_case_progress_count, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const getWipCounts = useApi<any>(
    webservice_url.fetch_live_case_progress_count,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const fetchData = async () => {
    // Dono API parallel chalengi, ek fail hui to dusri ka data phir bhi aayega
    const [totalRes, wipRes] = await Promise.allSettled([
      getTotalCounts.callApi({}),
      getWipCounts.callApi({}),
    ]);

    if (totalRes.status === "fulfilled") {
      setData(totalRes.value?.data ?? []);
    } else {
      console.error(totalRes.reason);
      setData([]);
    }

    if (wipRes.status === "fulfilled") {
      setWipData(wipRes.value?.data ?? []);
    } else {
      console.error(wipRes.reason);
      setWipData([]);
    }
  };

  const getCount = (label: string) =>
    data.find((item) => item?.label === label)?.count ?? 0;

  const getStatusId = (label: string) =>
    data.find((item) => item?.label === label)?.status_id ?? "";

  const totalInvitationsCount =
    data?.find((item) => item?.label === "Total")?.count ?? 0;

  // Work In Progress ke liye alag helpers (wipData se)
  const wipItem = wipData.find((item) => item?.label === "Work In Progress");
  const wipCount = wipItem?.count ?? 0;
  const wipStatusId = wipItem?.status_id ?? "";

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("tab")) {
      url.searchParams.delete("tab");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  // const ApplicantStats = [
  //   {
  //     id: "total_invite_sent",
  //     label: "Total Invitations",
  //     count: totalInvitationsCount,
  //     component: <TotalInviteSent countData={data} />,
  //   },
  //   {
  //     id: "pending_login",
  //     label: "Login Pending",
  //     count: getCount("Login Pending"),
  //     component: <PendingLogin status_id={getStatusId("Login Pending")} />,
  //   },
  //   {
  //     id: "logged_in",
  //     label: "Logged In",
  //     count: getCount("Logged In"),
  //     component: <ApplicantLoggedIn status_id={getStatusId("Logged In")} />,
  //   },
  //   {
  //     id: "in_progress",
  //     label: "In Progress",
  //     count: getCount("In Progress"),
  //     component: <ApplicantInProgress status_id={getStatusId("In Progress")} />,
  //   },
  //   {
  //     id: "workInProgress",
  //     label: "Live Case Progress",
  //     count: wipCount, // separate API se
  //     component: <WorkInProgress status_id={wipStatusId} />, // separate API se
  //   },
  // ];
  const ApplicantStats = [
    {
      id: "total_invite_sent",
      label: "Total Invitations",
      icon: <FileTextOutlined />,
      count: totalInvitationsCount,
      component: <TotalInviteSent countData={data} />,
    },
    {
      id: "pending_login",
      label: "Login Pending",
      icon: <ClockCircleOutlined />,
      count: getCount("Login Pending"),
      component: <PendingLogin status_id={getStatusId("Login Pending")} />,
    },
    {
      id: "logged_in",
      label: "Logged In",
      icon: <LoginOutlined />,
      count: getCount("Logged In"),
      component: <ApplicantLoggedIn status_id={getStatusId("Logged In")} />,
    },
    {
      id: "in_progress",
      label: "In Progress",
      icon: <SyncOutlined />,
      count: getCount("In Progress"),
      component: <ApplicantInProgress status_id={getStatusId("In Progress")} />,
    },
    {
      id: "workInProgress",
      label: "Live Case Progress",
      icon: <FundProjectionScreenOutlined />,
      count: wipCount,
      component: <WorkInProgress status_id={wipStatusId} />,
    },
  ];
  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [showModifyInvite, setShowModifyInvite] = useState<boolean>(false);
  const [showGuide, setShowGuide] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useState<string>(() => {
    try {
      return localStorage.getItem(ACTIVE_TAB_KEY) || "total_invite_sent";
    } catch {
      return "total_invite_sent";
    }
  });

  const activeItem = ApplicantStats.find((i) => i.id === activeTab);
  const activeComponent = activeItem ? activeItem.component : <></>;

  useEffect(() => {
    const valid = ApplicantStats.some((s) => s.id === activeTab);
    if (!valid && ApplicantStats[0]?.id) {
      setActiveTab(ApplicantStats[0].id);
    }
  }, [data, wipData]);

  // handle tab click
  const handleTabClick = (d: { id: string }) => {
    setActiveTab(d.id);
    try {
      localStorage.setItem(ACTIVE_TAB_KEY, d.id);
    } catch (err) {
      console.error("Failed to save active tab to localStorage:", err);
    }
  };

  const isLoading = getTotalCounts.loading || getWipCounts.loading;
  const CaseMark = () => (
    <svg
      className="tcp-case-mark"
      viewBox="0 0 40 32"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="14" width="4" height="18" rx="1.5" />
      <rect x="9" y="8" width="4" height="24" rx="1.5" />
      <rect x="18" y="0" width="4" height="32" rx="1.5" />
      <rect x="27" y="10" width="4" height="22" rx="1.5" />
      <rect x="36" y="18" width="4" height="14" rx="1.5" />
    </svg>
  );
  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="tcp-header-title-row mb-3">
          <CaseMark />
          <div>
            <h4 className="tcp-title">Candidate Tracking</h4>
            <p className="tcp-subtitle">
              Track candidate cases, monitor screening progress, and manage
              invitations from one workspace.
            </p>
          </div>
        </div>
        {isLoading ? (
          <PageLoader />
        ) : (
          <>
            {/* Tabs */}
            {/* <TabItems
              tabList={ApplicantStats}
              activeTab={activeTab}
              handleClick={handleTabClick}
            /> */}
            {/* <ul className="invite-tabs">
              {ApplicantStats.map((tab) => (
                <li key={tab.id}>
                  <button
                    type="button"
                    className={`invite-btn ${
                      activeTab === tab.id ? "active" : ""
                    }`}
                    onClick={() => handleTabClick(tab)}
                  >
                    <span className="d-block">{tab.label}</span>
                    {typeof tab.count === "number" && (
                      <span className="d-block fw-semibold">{tab.count}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul> */}
            <ul className="invite-tabs">
              {ApplicantStats.map((tab) => (
                <li key={tab.id}>
                  <button
                    type="button"
                    className={`invite-btn ${
                      activeTab === tab.id ? "active" : ""
                    }`}
                    onClick={() => handleTabClick(tab)}
                  >
                    <span className="d-flex align-items-center justify-content-center gap-2">
                      {tab.icon}
                      <span>{tab.label}</span>
                    </span>

                    {typeof tab.count === "number" && (
                      <span className="d-block fw-semibold">{tab.count}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>

            <div className="text-end">
              <Button
                className="border-secodary"
                onClick={() => setShowGuide((prev) => !prev)}
              >
                <i className="ti ti-info-circle-filled fs-16"></i>
                <div className="pb-1">Guide</div>
              </Button>
            </div>

            {/* Guide */}
            <GuideContentArea
              maxHeight={"400px"}
              showGuide={showGuide}
              handleClick={() => setShowGuide(false)}
              title={"Applicant Invited"}
              content={
                <div>
                  <div className="pb-2">
                    The applicant will receive the user credentials to access
                    the Aupertz applicant portal through an invite.
                  </div>
                  <div className="pb-4">
                    The applicant will log in to fill the applicant form (ADF)
                    and submit all the required data for screening.
                  </div>
                  {guideData?.map((d) => (
                    <div
                      className="mb-3"
                      style={{ maxWidth: "900px" }}
                      key={d.title}
                    >
                      <h6 className="fw-semibold pb-1">{d.title}</h6>
                      {d?.steps?.map((i, idx) => (
                        <div className="d-flex gap-2 py-1" key={idx}>
                          <div>{idx + 1}.</div>
                          <div>{i}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              }
            />

            {/* ActiveComponent */}
            <div className="mt-4">{activeComponent}</div>
          </>
        )}

        {showTimeline && (
          <TimelineModal
            timelineData={timelineData_invite}
            onClose={() => setShowTimeline(false)}
          />
        )}
        {showModifyInvite && (
          <ModifyInviteModal
            handleClose={() => setShowModifyInvite(false)}
            handleSubmit={(d) => {
              console.log(d);
              setShowModifyInvite(false);
            }}
          />
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ApplicantCaseTracking;

// import { useEffect, useState } from "react";
// import TotalInviteSent from "./applicant-invited/TotalInviteSent";
// import PendingLogin from "./applicant-invited/PendingLogin";
// import ApplicantLoggedIn from "./applicant-invited/ApplicantLoggedIn";
// import ModifyInviteModal from "../../../core/common/ModifyInviteModal";
// import TimelineModal from "../../../core/common/TimelineModal";
// import { Button } from "antd";
// import GuideContentArea from "../../../core/common/GuideContentArea";
// import { timelineData_invite } from "../../../core/data/js/dummy-data";
// import TabItems from "../../../core/common/TabItems";
// import Footer from "../../../core/common/Footer";
// import useApi from "../../../core/customhook/useApi";
// import ApplicantInProgress from "./applicant-invited/ApplicantInProgress";
// import { PageLoader } from "../../../core/common/loader/PageLoader";
// import webservice_url from "../../../core/webservice/Webservice_url";

// interface CountData {
//   status_id: string;
//   label: string;
//   count: number;
// }

// const guideData = [
//   {
//     title: "Stage 1 - Total Invites Sent",
//     steps: [
//       "All invites sent will by default show a pending login status. The invite will be sent to the candidate, and they have not yet opened the link and logged in to the account.",
//     ],
//   },
//   {
//     title: "Stage 2 - Pending LogIn",
//     steps: [
//       "The invite will be sent to the candidate, and they have not yet opened the link and logged in to the account.",
//     ],
//   },
//   {
//     title: "Stage 3 - Logged In",
//     steps: [
//       "The invite has been opened by the candidate, and they have proceeded to fill the form. Form filling is in progress.",
//     ],
//   },
//   {
//   title: "Stage 4 - In Progress",
//   steps: [
//     "The case has been received by the agency and the background verification process has started.",
//     "The agency/verifier is actively working on the case and completing the required verification checks.",
//     "The progress of individual checks can be tracked from the case details.",
//     "Once all required checks are completed, the case will move to the next applicable stage based on the verification outcome.",
//   ],
// },
// ];

// const ACTIVE_TAB_KEY = "adminCaseTrackActiveTab";

// const ApplicantCaseTracking = () => {
//   const [data, setData] = useState<CountData[]>([]);
//   const getTotalCounts = useApi<any>(
//     webservice_url.admin_case_progress_count,
//     {
//       method: "GET",
//       autoCall: false,
//       handleGlobalError: false,
//     },
//   );

//   const fetchData = async () => {
//     try {
//       const res = await getTotalCounts.callApi({});
//       setData(res?.data ?? []);
//     } catch (err) {
//       console.error(err);
//       setData([]);
//     }
//   };

//   const getCount = (label: string) =>
//     data.find((item) => item?.label === label)?.count ?? 0;

//   const getStatusId = (label: string) =>
//     data.find((item) => item?.label === label)?.status_id ?? "";

// const totalInvitationsCount =
//   data?.find((item) => item?.label === "Total")?.count ?? 0;

//   useEffect(() => {
//     fetchData();
//   }, []);

//   useEffect(() => {
//     const url = new URL(window.location.href);
//     if (url.searchParams.has("tab")) {
//       url.searchParams.delete("tab");
//       window.history.replaceState({}, "", url.toString());
//     }
//   }, []);

//   const ApplicantStats = [
//     {
//       id: "total_invite_sent",
//       label: "Total Invitations",
//       count: totalInvitationsCount,
//       component: <TotalInviteSent countData={data} />,
//     },
//     // {
//     //   id: "pre_bgv",
//     //   label: "Pre BGV",
//     //   count: getCount("Pre BGV"),
//     //   component: <PreBgv />,
//     // },
//     {
//       id: "pending_login",
//       label: "Login Pending",
//       count: getCount("Login Pending"),
//       component: <PendingLogin status_id={getStatusId("Login Pending")} />,
//     },
//     {
//       id: "logged_in",
//       label: "Logged In",
//       count: getCount("Logged In"),
//       component: <ApplicantLoggedIn status_id={getStatusId("Logged In")} />,
//     },
//     {
//       id: "in_progress",
//       label: "In Progress",
//       count: getCount("In Progress"),
//       component: <ApplicantInProgress status_id={getStatusId("In Progress")} />,
//     },
//   ];

//   const [showTimeline, setShowTimeline] = useState<boolean>(false);
//   const [showModifyInvite, setShowModifyInvite] = useState<boolean>(false);
//   const [showGuide, setShowGuide] = useState<boolean>(false);

//   const [activeTab, setActiveTab] = useState<string>(() => {
//     try {
//       return localStorage.getItem(ACTIVE_TAB_KEY) || "total_invite_sent";
//     } catch {
//       return "total_invite_sent";
//     }
//   });

//   const activeItem = ApplicantStats.find((i) => i.id === activeTab);
//   const activeComponent = activeItem ? activeItem.component : <></>;

//    useEffect(() => {
//     const valid = ApplicantStats.some((s) => s.id === activeTab);
//     if (!valid && ApplicantStats[0]?.id) {
//       setActiveTab(ApplicantStats[0].id);
//     }
//   }, [data]);

//   // handle tab click
//   const handleTabClick = (d: { id: string }) => {
//     setActiveTab(d.id);
//     try {
//       localStorage.setItem(ACTIVE_TAB_KEY, d.id);
//     } catch (err) {
//       console.error("Failed to save active tab to localStorage:", err);
//     }
//   };

//   return (
//     <div className="page-wrapper">
//       <div className="content">
//         {getTotalCounts.loading
//           ? <PageLoader />
//           : <>
//             {/* Tabs */}
//             <TabItems
//               tabList={ApplicantStats}
//               activeTab={activeTab}
//               handleClick={handleTabClick}
//             />

//             <div className="text-end">
//               <Button
//                 className="border-secodary"
//                 onClick={() => setShowGuide((prev) => !prev)}
//               >
//                 <i className="ti ti-info-circle-filled fs-16"></i>
//                 <div className="pb-1">Guide</div>
//               </Button>
//             </div>

//             {/* Guide */}
//             <GuideContentArea
//               maxHeight={"400px"}
//               showGuide={showGuide}
//               handleClick={() => setShowGuide(false)}
//               title={"Applicant Invited"}
//               content={
//                 <div>
//                   <div className="pb-2">
//                     The applicant will receive the user credentials to access the
//                     Aupertz applicant portal through an invite.
//                   </div>
//                   <div className="pb-4">
//                     The applicant will log in to fill the applicant form (ADF) and
//                     submit all the required data for screening.
//                   </div>
//                   {guideData?.map((d) => (
//                     <div
//                       className="mb-3"
//                       style={{ maxWidth: "900px" }}
//                       key={d.title}
//                     >
//                       <h6 className="fw-semibold pb-1">{d.title}</h6>
//                       {d?.steps?.map((i, idx) => (
//                         <div className="d-flex gap-2 py-1" key={idx}>
//                           <div>{idx + 1}.</div>
//                           <div>{i}</div>
//                         </div>
//                       ))}
//                     </div>
//                   ))}
//                 </div>
//               }
//             />

//             {/* ActiveComponent */}
//             <div className="mt-4">{activeComponent}</div>
//           </>}

//         {showTimeline && (
//           <TimelineModal
//             timelineData={timelineData_invite}
//             onClose={() => setShowTimeline(false)}
//           />
//         )}
//         {showModifyInvite && (
//           <ModifyInviteModal
//             handleClose={() => setShowModifyInvite(false)}
//             handleSubmit={(d) => {
//               console.log(d);
//               setShowModifyInvite(false);
//             }}
//           />
//         )}
//       </div>

//       <Footer />
//     </div>
//   );
// };

// export default ApplicantCaseTracking;
